import { FashionOrderProductItemInput, ProductApiData, SizeQuantities } from './types'

export const compareSizes = (a: string, b: string): number => {
  const isNumA = /^\d+$/.test(a);
  const isNumB = /^\d+$/.test(b);

  if (isNumA && isNumB) {
    return Number(a) - Number(b);
  }
  if (isNumA && !isNumB) {
    return -1;
  }
  if (!isNumA && isNumB) {
    return 1;
  }

  const textOrder = [
    'PP', 'P', 'M', 'G', 'GG', 'XL', 'XLL',
    'G1', 'G2', 'G3', 'G4',
    'TAMANHO ÚNICO', 'ÚNICO', 'U'
  ];

  const normA = a.trim().toUpperCase();
  const normB = b.trim().toUpperCase();

  const idxA = textOrder.indexOf(normA);
  const idxB = textOrder.indexOf(normB);

  if (idxA !== -1 && idxB !== -1) {
    return idxA - idxB;
  }
  if (idxA !== -1 && idxB === -1) {
    return -1;
  }
  if (idxA === -1 && idxB !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};

export const cleanCurrency = (value: any): number => {
  const num = Number(String(value || '').replace(/[^0-9,-]+/g, '').replace(',', '.'))
  return isNaN(num) ? 0 : num
}

export const centsDigitsToReais = (value: unknown): number => {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (!digits) return 0
  return Number(digits) / 100
}

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value)

export const formatCentDigitsForInput = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(digits) / 100)
}

export const formatDate = (iso: string | null | undefined): string =>
  iso ? iso.substring(0, 10) : ''

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
}

export const getSafeValue = (value: any): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return ('name' in value ? value.name : null) || '-'
  return String(value) || '-'
}

export const countColors = (product: ProductApiData): number => {
  if (product.colorCount && product.colorCount > 0) return product.colorCount
  if (product.items?.length) return new Set(product.items.map((i) => i.colorId)).size
  return product.colors?.length ?? 0
}

export const normalizeProductData = (product: any): ProductApiData => {
  const sizeQuantities: SizeQuantities = {}
  const items = [...(product.items || [])] as FashionOrderProductItemInput[]
  items.sort((a, b) => compareSizes(a.size, b.size))
  items.forEach((item: FashionOrderProductItemInput) => {
    if (item.size && item.quantity !== undefined)
      sizeQuantities[item.size] = (sizeQuantities[item.size] || 0) + Number(item.quantity)
  })
  const calculatedItems = Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0)
  const totalItems = calculatedItems > 0 ? calculatedItems : (Number(product.totalItems) || 0)
  const unitPrice = Number(product.fashionProduct?.price || product.price || 0)
  const rawSubtotal = product.subtotal != null ? Number(product.subtotal) : 0
  const subtotal = rawSubtotal > 0 ? rawSubtotal : totalItems * unitPrice
  const colorCount = product.colorCount || new Set(items.map((i: any) => i.colorId)).size

  return {
    ...product,
    sizeQuantities,
    totalItems,
    subtotal,
    colorCount,
    items,
    kitIndex: product.kitIndex !== undefined ? product.kitIndex : null,
  }
}
